import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import allActions from '../../../actions';
import { makeStyles } from '@material-ui/core/styles';
import {
    IconButton,
    Typography,
    TextField,
    MenuItem,
    CircularProgress,
    Box,
    Paper,
} from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import CloseIcon from '@material-ui/icons/Close';
import UnoButton from '../../utils/UnoButton';
import { alignAudespCodes } from '../../../API/fund';
import moment from 'moment';

const useStyles = makeStyles((theme) => ({
    root: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
    },
    header: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: theme.spacing(3),
        borderBottom: '1px solid #ccc',
    },
    main: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        padding: '24px',
        overflowY: 'scroll',
    },
    formContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        maxWidth: '500px',
        margin: '0 auto',
        width: '100%',
    },
    fileInput: {
        marginTop: '8px',
    },
    buttonsRow: {
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: '24px',
        gap: '12px',
    },
    loadingContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px',
    },
    resultContainer: {
        marginTop: '24px',
        padding: '16px',
        backgroundColor: '#f5f5f5',
        borderRadius: '8px',
        maxHeight: '300px',
        overflow: 'auto',
    },
    resultTitle: {
        fontWeight: 'bold',
        marginBottom: '8px',
    },
    errorText: {
        color: '#d32f2f',
    },
}));

const MAX_FILE_SIZE = 5 * 1024 * 1024;

export default function AlignAudespModal(props) {
    const { content } = props;
    const classes = useStyles();
    const dispatch = useDispatch();

    const clientOn = useSelector(state => state.clientOn);
    const [month, setMonth] = useState(clientOn?.client?.selectedPortfolioMonth || 1);
    const [year, setYear] = useState(clientOn?.client?.selectedPortfolioYear || new Date().getFullYear());
    const [selectedFile, setSelectedFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    const months = [
        { value: 1, label: 'Janeiro' },
        { value: 2, label: 'Fevereiro' },
        { value: 3, label: 'Março' },
        { value: 4, label: 'Abril' },
        { value: 5, label: 'Maio' },
        { value: 6, label: 'Junho' },
        { value: 7, label: 'Julho' },
        { value: 8, label: 'Agosto' },
        { value: 9, label: 'Setembro' },
        { value: 10, label: 'Outubro' },
        { value: 11, label: 'Novembro' },
        { value: 12, label: 'Dezembro' },
    ];

    const years = Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i);

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        
        if (!file) return;

        if (!file.name.toLowerCase().endsWith('.xml')) {
            setError('Apenas arquivos .xml são permitidos.');
            setSelectedFile(null);
            return;
        }

        if (file.size > MAX_FILE_SIZE) {
            setError('Arquivo muito grande. Máximo 5MB.');
            setSelectedFile(null);
            return;
        }

        setError(null);
        setSelectedFile(file);
    };

    const downloadJson = (jsonData) => {
        const jsonString = JSON.stringify(jsonData, null, 2);
        const blob = new Blob([jsonString], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `ALINHAMENTO_AUDESP_${moment().format('YYYYMMDD_HHmmss')}.txt`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    };

    const handleAlign = async () => {
        if (!selectedFile) {
            setError('Selecione um arquivo XML.');
            return;
        }

        setLoading(true);
        setResult(null);
        setError(null);

        try {
            const reader = new FileReader();
            
            reader.onload = async (e) => {
                const xmlContent = e.target.result;
                
                const response = await alignAudespCodes(
                    content.client.id,
                    month,
                    year,
                    xmlContent
                );

                setLoading(false);

                if (response.success) {
                    setResult(response.body);
                    downloadJson(response.body);
                    dispatch(allActions.notificationActions.showNotification({
                        message: 'Alinhamento concluído com sucesso!',
                        type: 'success'
                    }));
                } else {
                    setError(response.body?.message || 'Erro ao realizar alinhamento.');
                }
            };

            reader.onerror = () => {
                setLoading(false);
                setError('Erro ao ler arquivo.');
            };

            reader.readAsText(selectedFile);
        } catch (err) {
            setLoading(false);
            setError('Erro ao realizar alinhamento.');
        }
    };

    const handleClose = () => {
        dispatch(allActions.rightContentActions.setRightContent(null));
    };

    return (
        <div className={classes.root}>
            <div className={classes.header}>
                <IconButton onClick={handleClose}>
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant={'h6'}>
                    {'Alinhar Códigos Audesp'}
                </Typography>
                <IconButton onClick={handleClose}>
                    <CloseIcon />
                </IconButton>
            </div>

            <div className={classes.main}>
                <div className={classes.formContainer}>
                    <Typography variant="subtitle1" color="textSecondary">
                        Selecione o período de referência e faça upload do arquivo XML para comparar os códigos Audesp com os fundos e títulos cadastrados no sistema.
                    </Typography>

                    <TextField
                        select
                        label="Mês"
                        value={month}
                        onChange={(e) => setMonth(e.target.value)}
                        fullWidth
                    >
                        {months.map((m) => (
                            <MenuItem key={m.value} value={m.value}>
                                {m.label}
                            </MenuItem>
                        ))}
                    </TextField>

                    <TextField
                        select
                        label="Ano"
                        value={year}
                        onChange={(e) => setYear(e.target.value)}
                        fullWidth
                    >
                        {years.map((y) => (
                            <MenuItem key={y} value={y}>
                                {y}
                            </MenuItem>
                        ))}
                    </TextField>

                    <Paper variant="outlined" style={{ padding: '16px' }}>
                        <Typography variant="body2" color="textSecondary">
                            Selecione o arquivo XML (máx 5MB)
                        </Typography>
                        <input
                            type="file"
                            accept=".xml"
                            onChange={handleFileChange}
                            className={classes.fileInput}
                        />
                        {selectedFile && (
                            <Typography variant="body2" style={{ marginTop: '8px', color: 'green' }}>
                                Arquivo selecionado: {selectedFile.name}
                            </Typography>
                        )}
                    </Paper>

                    {error && (
                        <Typography variant="body2" className={classes.errorText}>
                            {error}
                        </Typography>
                    )}

                    {loading && (
                        <div className={classes.loadingContainer}>
                            <CircularProgress />
                            <Typography style={{ marginLeft: '16px' }}>
                                Processando alinhamento...
                            </Typography>
                        </div>
                    )}

                    {result && (
                        <div className={classes.resultContainer}>
                            <Typography className={classes.resultTitle}>
                                Resultado do Alinhamento:
                            </Typography>
                            <Box component="pre" style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>
                                {JSON.stringify(result, null, 2)}
                            </Box>
                        </div>
                    )}

                    <div className={classes.buttonsRow}>
                        <UnoButton
                            onClick={handleClose}
                            type={'secondary'}>
                            {'Cancelar'}
                        </UnoButton>
                        <UnoButton
                            onClick={handleAlign}
                            type={'primary'}
                            disabled={!selectedFile || loading}>
                            {loading ? 'Processando...' : 'Alinhar e Baixar'}
                        </UnoButton>
                    </div>
                </div>
            </div>
        </div>
    );
}